import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import TemplateSelector from '@/components/whatsapp/TemplateSelector';
import MonitoreoTab from '@/components/MonitoreoTab';
import FieldTooltip from '@/components/ui/FieldTooltip';
import { WA_VARIABLE_SOURCES, extractTemplateIndexes, normalizeVariableMapping, sourceExample } from '@/lib/waVariableMapping';
import { Workflow, Plus, Pencil, Trash2, Loader2, Power, MessageSquare, ClipboardList, MessageCircle, Clock, UserCheck, Mail, Image as ImageIcon, Phone } from 'lucide-react';

const KIND_FORM = 'formulario';
const KIND_C2W = 'click_to_whatsapp';
const MAX_STEPS = { [KIND_FORM]: 3, [KIND_C2W]: 5 };
const DAY_OPTS = [
  { v: 1, l: 'Lun' }, { v: 2, l: 'Mar' }, { v: 3, l: 'Mié' },
  { v: 4, l: 'Jue' }, { v: 5, l: 'Vie' }, { v: 6, l: 'Sáb' }, { v: 0, l: 'Dom' },
];

const emptyHours = () => ({
  enabled: true, start: '09:00', end: '19:00',
  timezone: 'America/Mexico_City', days: [1, 2, 3, 4, 5],
});

const EMPTY_FORM = {
  name: '', description: '', cancel_on_reply: true,
  kind: KIND_FORM, campaign_ids: [], hours: emptyHours(),
  assignment_policy: 'always', lost_stage_key: 'perdido', steps: [],
};

const newStep = (kind) => (
  kind === KIND_C2W
    ? { delay_minutes: 60, channel: 'whatsapp', text: '', media_url: '', media_type: '', template: null }
    : { delay_minutes: 120, channel: 'whatsapp', template: null }
);

const hydrateSteps = (steps) => (Array.isArray(steps) ? steps : []).map((s) => ({
  delay_minutes: Number(s?.delay_minutes ?? 0),
  channel: s?.channel || 'whatsapp',
  when: s?.when || 'cualquiera',
  solo_si: s?.solo_si || 'siempre',
  template: s?.template || null,
  text: s?.text || '',
  media_url: s?.media_url || '',
  media_type: s?.media_type || '',
  email_template_id: s?.email_template_id || '',
  flow_id: s?.flow_id || '',
  flow_app_id: s?.flow_app_id || '',
  flow_recipient: s?.flow_recipient || 'lead',
  flow_cta: s?.flow_cta || '',
  flow_screen: s?.flow_screen || '',
}));

const recipePreview = (body, mapping) => {
  const rows = Array.isArray(mapping) ? mapping : [];
  if (body) {
    let out = String(body);
    rows.forEach((row) => {
      const idx = Number(row?.index);
      if (!Number.isFinite(idx) || idx <= 0) return;
      out = out.replace(new RegExp(`\\{\\{\\s*${idx}\\s*\\}\\}`, 'g'), sourceExample(row.source, row.custom_value));
    });
    return out;
  }
  return rows.map((row) => `{{${row.index}}} → ${sourceExample(row.source, row.custom_value)}`).join('  ·  ');
};

const kindLabel = (k) => (k === KIND_C2W ? 'Te escribió por WhatsApp' : 'Llenó un formulario');

export default function RecipesPage() {
  const [recipes, setRecipes] = useState([]);
  const [wabas, setWabas] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [emails, setEmails] = useState([]);
  const [emailOk, setEmailOk] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [wabaSel, setWabaSel] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [myFlows, setMyFlows] = useState([]);

  const load = useCallback(async () => {
    try {
      const [r, gup, tw, camps, mail] = await Promise.all([
        api.get('/comm-logic-recipes'),
        api.get('/whatsapp/gupshup/numbers').catch(() => ({ data: { numbers: [] } })),
        api.get('/waba-accounts').catch(() => ({ data: [] })),
        api.get('/campaigns').catch(() => ({ data: [] })),
        api.get('/correo/plantillas').catch(() => ({ data: null })),
      ]);
      setRecipes(Array.isArray(r.data) ? r.data : []);
      const gupWabas = (Array.isArray(gup.data?.numbers) ? gup.data.numbers : []).map((w) => ({
        id: w.id, provider: 'gupshup', label: `${w.app_name || w.id} — ${w.source_phone || 'sin número'}`,
      }));
      const twWabas = (Array.isArray(tw.data) ? tw.data : [])
        .filter((w) => String(w.provider || '').toLowerCase() === 'twilio')
        .map((w) => ({ id: w.id, provider: 'twilio', label: `${w.name || w.id} — ${w.phone_number || 'sin número'}` }));
      setWabas([...gupWabas, ...twWabas]);
      const campList = Array.isArray(camps.data) ? camps.data : (camps.data?.campaigns || []);
      setCampaigns(campList.filter((c) => c && c.id));
      if (mail.data?.plantillas) {
        setEmails(mail.data.plantillas);
        setEmailOk(true);
      }
    } catch { /* silencioso */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/whatsapp/flows/mine').then((r) => setMyFlows(r.data?.flows || [])).catch(() => setMyFlows([])); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, hours: emptyHours(), steps: [newStep(KIND_FORM)] });
    setWabaSel(wabas[0] || null);
    setDialog(true);
  };

  // §F Plantilla de ejemplo: demuestra las 3 piezas de la campaña de llamada —
  // conference en horario, IA de voz fuera de horario, y rescate por IA si no contestó.
  const openExample = () => {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      name: 'Llamada: vendedor en horario, IA fuera / si no contesta',
      description: 'En horario te enlazamos con el vendedor; fuera de horario contesta la IA de voz; y si el vendedor no contestó, la IA de voz retoma al lead. Edítala a tu gusto. Los pasos de voz requieren plan con voz.',
      kind: KIND_C2W,
      hours: emptyHours(),
      cancel_on_reply: true,
      steps: [
        { delay_minutes: 0, channel: 'conference', when: 'dentro_horario', solo_si: 'siempre', text: '', template: null, media_url: '', media_type: '', email_template_id: '' },
        { delay_minutes: 0, channel: 'llamada_ia', when: 'fuera_horario', solo_si: 'siempre', text: 'Atender al lead fuera de horario: confirmar interes y agendar seguimiento.', template: null, media_url: '', media_type: '', email_template_id: '' },
        { delay_minutes: 15, channel: 'llamada_ia', when: 'cualquiera', solo_si: 'sin_respuesta', text: 'Si el vendedor no contesto: retomar al lead y agendar.', template: null, media_url: '', media_type: '', email_template_id: '' },
      ],
    });
    setWabaSel(wabas[0] || null);
    setDialog(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || '',
      description: r.description || '',
      cancel_on_reply: r.cancel_on_reply !== false,
      kind: r.kind === KIND_C2W ? KIND_C2W : KIND_FORM,
      campaign_ids: Array.isArray(r.campaign_ids) ? r.campaign_ids : [],
      hours: { ...emptyHours(), ...(r.hours || {}) },
      assignment_policy: r.assignment_policy || 'always',
      lost_stage_key: r.lost_stage_key || 'perdido',
      steps: hydrateSteps(r.steps).length ? hydrateSteps(r.steps) : [newStep(r.kind)],
    });
    setWabaSel(wabas[0] || null);
    setDialog(true);
  };

  const setKind = (kind) => setForm((f) => ({
    ...f, kind, steps: f.steps.length ? f.steps : [newStep(kind)],
  }));
  const updateStep = (i, patch) => setForm((f) => ({
    ...f, steps: f.steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
  }));
  const addStep = () => {
    const cap = MAX_STEPS[form.kind] || 3;
    if (form.steps.length >= cap) {
      toast.error(`Máximo ${cap} mensajes en 24 horas para este tipo de receta.`);
      return;
    }
    setForm((f) => ({ ...f, steps: [...f.steps, newStep(f.kind)] }));
  };
  const removeStep = (i) => setForm((f) => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));
  const setMappingRow = (i, idx, patch) => setForm((f) => ({
    ...f,
    steps: f.steps.map((s, sidx) => {
      if (sidx !== i || !s.template) return s;
      const variable_mapping = (s.template.variable_mapping || []).map((row) => (
        Number(row.index) === idx ? { ...row, ...patch } : row
      ));
      return { ...s, template: { ...s.template, variable_mapping } };
    }),
  }));

  const toggleDay = (d) => setForm((f) => {
    const days = f.hours.days.includes(d) ? f.hours.days.filter((x) => x !== d) : [...f.hours.days, d];
    return { ...f, hours: { ...f.hours, days: days.length ? days : [1] } };
  });

  const toggleCampaign = (id) => setForm((f) => {
    const has = f.campaign_ids.includes(id);
    return { ...f, campaign_ids: has ? f.campaign_ids.filter((x) => x !== id) : [...f.campaign_ids, id] };
  });

  const uploadMedia = async (i, file) => {
    if (!file) return;
    setUploading(i);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('authorized_vendor_ids', '[]');
      const { data } = await api.post('/file-library', fd);
      const fileId = data?.file_id;
      if (!fileId) { toast.error('Se subió, pero no recibí el id. Inténtalo otra vez.'); return; }
      const media_type = file.type.startsWith('video') ? 'video' : 'image';
      updateStep(i, { media_url: `library:${fileId}`, media_type });
      toast.success('Archivo guardado en tu biblioteca.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo subir. Revisa que la biblioteca esté activa.');
    } finally { setUploading(null); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Ponle un nombre que tu equipo entienda. Ej: “Formularios de Facebook”.'); return; }
    if (!form.steps.length) { toast.error('Agrega al menos un mensaje.'); return; }
    const cap = MAX_STEPS[form.kind] || 3;
    if (form.steps.length > cap) { toast.error(`Máximo ${cap} mensajes.`); return; }
    const totalDelay = form.steps.reduce((a, s) => a + (Number(s.delay_minutes) || 0), 0);
    if (totalDelay > 1440) { toast.error('Todo debe caber en 24 horas. Acorta las esperas.'); return; }
    for (let i = 0; i < form.steps.length; i += 1) {
      const s = form.steps[i];
      if (s.channel === 'llamada_ia' || s.channel === 'conference') {
        continue;
      }
      if (s.channel === 'email') {
        if (!s.email_template_id) { toast.error(`Paso ${i + 1}: elige un correo.`); return; }
        continue;
      }
      if (s.channel === 'flow') {
        if (!s.flow_id) { toast.error(`Paso ${i + 1}: elige un flow publicado.`); return; }
        continue;
      }
      if (form.kind === KIND_FORM && !s.template?.stable_ref) {
        toast.error(`Paso ${i + 1}: elige una plantilla aprobada.`);
        return;
      }
      if (form.kind === KIND_C2W && !String(s.text || '').trim() && !s.media_url) {
        toast.error(`Paso ${i + 1}: escribe el mensaje o adjunta una foto.`);
        return;
      }
    }
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      trigger: 'lead_incoming',
      cancel_on_reply: form.cancel_on_reply,
      kind: form.kind,
      campaign_ids: form.campaign_ids,
      hours: form.hours,
      assignment_policy: form.assignment_policy,
      lost_stage_key: form.lost_stage_key,
      steps: form.steps.map((s) => ({
        delay_minutes: Number(s.delay_minutes) || 0,
        channel: s.channel || 'whatsapp',
        when: s.when || 'cualquiera',
        solo_si: s.solo_si || 'siempre',
        template: (s.channel === 'email' || s.channel === 'llamada_ia' || s.channel === 'conference' || s.channel === 'flow') ? null : (s.template || null),
        text: s.channel === 'llamada_ia' ? (s.text || '') : (form.kind === KIND_C2W && s.channel !== 'flow' ? (s.text || '') : null),
        media_url: s.channel === 'flow' ? null : (s.media_url || null),
        media_type: s.channel === 'flow' ? null : (s.media_type || null),
        email_template_id: s.channel === 'email' ? s.email_template_id : null,
        flow_id: s.channel === 'flow' ? (s.flow_id || '') : undefined,
        flow_app_id: s.channel === 'flow' ? (s.flow_app_id || '') : undefined,
        flow_recipient: s.channel === 'flow' ? (s.flow_recipient || 'lead') : undefined,
        flow_cta: s.channel === 'flow' ? (s.flow_cta || 'Abrir') : undefined,
        flow_screen: s.channel === 'flow' ? (s.flow_screen || '') : undefined,
      })),
    };
    try {
      if (editing) await api.put(`/comm-logic-recipes/${editing.id}`, payload);
      else await api.post('/comm-logic-recipes', payload);
      toast.success(editing ? 'Receta actualizada. Los seguimientos que ya van en camino no cambian.' : 'Receta creada. Actívala para que empiece a usarse.');
      setDialog(false); setEditing(null); setForm(EMPTY_FORM); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar la receta.'); }
  };

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar la receta "${r.name}"? Los seguimientos ya en camino no se cancelan.`)) return;
    try { await api.delete(`/comm-logic-recipes/${r.id}`); toast.success('Receta eliminada.'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo eliminar.'); }
  };

  const activate = async (r) => {
    if (!window.confirm(`Esto apaga la otra receta activa de tipo “${kindLabel(r.kind)}”. ¿Seguir?`)) return;
    try { await api.put(`/comm-logic-recipes/${r.id}/activate`); toast.success(`“${r.name}” ya está activa.`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo activar.'); }
  };

  const campsForKind = campaigns.filter((c) => {
    const t = String(c.link_type || '').toLowerCase();
    return form.kind === KIND_C2W ? t === 'whatsapp' : t !== 'whatsapp';
  });

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="recipes-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2"><Workflow className="w-5 h-5 text-violet-400" />Reglas de Comunicación</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Qué le decimos a un lead que se queda callado — según si llenó un formulario o nos escribió por WhatsApp.
          </p>
        </div>
        <Button size="sm" variant="outline" className="text-xs mr-2" onClick={openExample} data-testid="example-recipe-btn"><Phone className="w-3.5 h-3.5 mr-1" />Ejemplo con llamada</Button>
        <Button size="sm" className="text-xs" onClick={openNew} data-testid="new-recipe-btn"><Plus className="w-3.5 h-3.5 mr-1" />Nueva receta</Button>
      </div>

      <Tabs defaultValue="recetas">
        <TabsList data-testid="logicas-tabs">
          <TabsTrigger value="recetas" className="text-xs" data-testid="tab-recetas">Recetas</TabsTrigger>
          <TabsTrigger value="monitoreo" className="text-xs" data-testid="tab-monitoreo">Monitoreo</TabsTrigger>
        </TabsList>

        <TabsContent value="recetas" className="space-y-3">
          {recipes.length === 0 ? (
            <Card className="bg-card/50 backdrop-blur-md border-white/5"><CardContent className="py-12 text-center space-y-3">
              <Workflow className="w-10 h-10 text-muted-foreground mx-auto" />
              <p className="text-sm text-white">Aún no tienes recetas.</p>
              <p className="text-xs text-muted-foreground max-w-md mx-auto">
                Ejemplo: María llena el form de Facebook y no contesta el primer WhatsApp. La receta le manda hasta 3 recordatorios en el día, solo en horario de oficina.
              </p>
              <Button size="sm" className="text-xs" onClick={openNew}>Crear primera receta</Button>
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {recipes.map((r) => (
                <Card key={r.id} className="bg-card/50 backdrop-blur-md border-white/5" data-testid={`recipe-card-${r.id}`}>
                  <CardContent className="flex items-center justify-between px-4 py-3 gap-3 flex-wrap">
                    <div className="space-y-0.5 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{r.name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground flex-wrap">
                        <span>{kindLabel(r.kind)}</span>
                        <span>· {(r.steps || []).length} mensajes</span>
                        <span>· {r.hours?.enabled === false ? 'sin horario (24 h)' : `horario ${r.hours?.start || '09:00'}–${r.hours?.end || '19:00'}`}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[9px] ${r.is_active ? 'text-emerald-400 border-emerald-500/20' : 'text-muted-foreground border-white/10'}`}>{r.is_active ? 'Activa' : 'Apagada'}</Badge>
                      {!r.is_active && (
                        <Button variant="ghost" size="sm" className="h-7 text-[10px] text-emerald-400" onClick={() => activate(r)} data-testid={`activate-recipe-${r.id}`}><Power className="w-3 h-3 mr-1" />Activar</Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(r)} data-testid={`edit-recipe-${r.id}`}><Pencil className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => remove(r)} data-testid={`delete-recipe-${r.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="monitoreo"><MonitoreoTab /></TabsContent>
      </Tabs>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#0f1117] border-white/10 text-white max-w-lg max-h-[88vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="text-sm">{editing ? 'Editar receta' : 'Nueva receta'}</DialogTitle>
            <DialogDescription className="text-[11px] text-muted-foreground">
              Configúralo como se lo explicarías a Ana en recepción. Los seguimientos que ya van en camino no cambian.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs inline-flex items-center gap-1.5">
                ¿Cómo llegó este lead?
                <FieldTooltip tip="No es lo mismo. Si llenó un formulario, WhatsApp exige plantilla aprobada. Si él te escribió, puedes contestarle con texto, foto o video." />
              </Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <button type="button" onClick={() => setKind(KIND_FORM)} data-testid="recipe-kind-form"
                  className={`rounded-lg border p-3 text-left ${form.kind === KIND_FORM ? 'border-violet-400/50 bg-violet-500/10' : 'border-white/10 bg-black/20'}`}>
                  <ClipboardList className="w-4 h-4 text-violet-300 mb-1" />
                  <p className="text-[11px] font-semibold">Llenó un formulario</p>
                  <p className="text-[9px] text-muted-foreground mt-0.5">Hasta 3 plantillas en 24 h. Ej: landing de Facebook.</p>
                </button>
                <button type="button" onClick={() => setKind(KIND_C2W)} data-testid="recipe-kind-c2w"
                  className={`rounded-lg border p-3 text-left ${form.kind === KIND_C2W ? 'border-emerald-400/50 bg-emerald-500/10' : 'border-white/10 bg-black/20'}`}>
                  <MessageCircle className="w-4 h-4 text-emerald-300 mb-1" />
                  <p className="text-[11px] font-semibold">Escribió por WhatsApp</p>
                  <p className="text-[9px] text-muted-foreground mt-0.5">Hasta 5 mensajes libres. La IA ya contestó y se calló.</p>
                </button>
              </div>
            </div>

            <div>
              <Label className="text-xs">Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/20 border-white/10 text-xs mt-1" placeholder={form.kind === KIND_C2W ? 'Ej. Silencio después de la IA' : 'Ej. Formularios de Facebook'} data-testid="recipe-name" />
            </div>

            <div className="rounded-lg border border-amber-400/30 bg-amber-500/5 p-3 space-y-2" data-testid="recipe-hours-box">
              <div className="flex items-center justify-between">
                <Label className="text-xs inline-flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-amber-300" /> Horario en que se mandan los mensajes
                  <FieldTooltip tip="Si un mensaje caería a las 11 de la noche, se espera al día siguiente a la hora de apertura. Así no le escribes a María a deshoras." />
                </Label>
                <Switch checked={!!form.hours.enabled} onCheckedChange={(v) => setForm({ ...form, hours: { ...form.hours, enabled: v } })} data-testid="recipe-hours-enabled" />
              </div>
              <p className="text-[10px] text-muted-foreground">
                {form.hours.enabled
                  ? `Se respetan ${form.hours.start}–${form.hours.end} (México). Fuera de eso, espera al siguiente día laboral.`
                  : 'Apagado: se manda a cualquier hora, solo cuenta la espera en minutos.'}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[10px] text-muted-foreground">Abre</Label><Input type="time" value={form.hours.start} onChange={(e) => setForm({ ...form, hours: { ...form.hours, enabled: true, start: e.target.value } })} className="bg-black/30 border-white/10 text-xs h-8 mt-0.5" data-testid="recipe-hours-start" /></div>
                <div><Label className="text-[10px] text-muted-foreground">Cierra</Label><Input type="time" value={form.hours.end} onChange={(e) => setForm({ ...form, hours: { ...form.hours, enabled: true, end: e.target.value } })} className="bg-black/30 border-white/10 text-xs h-8 mt-0.5" data-testid="recipe-hours-end" /></div>
              </div>
              <div className="flex flex-wrap gap-1">
                {DAY_OPTS.map((d) => (
                  <button key={d.v} type="button" onClick={() => toggleDay(d.v)}
                    className={`text-[10px] px-2 py-1 rounded-md border ${form.hours.days.includes(d.v) ? 'border-amber-400/40 bg-amber-500/15' : 'border-white/10 text-muted-foreground'}`}>
                    {d.l}
                  </button>
                ))}
              </div>
            </div>

            {campsForKind.length > 0 && (
              <div>
                <Label className="text-xs inline-flex items-center gap-1.5">
                  ¿A qué campañas aplica?
                  <FieldTooltip tip="Si no marcas ninguna, aplica a todas las campañas de este tipo. Marca solo las que quieras (ej. “Preventa Torre 2”)." />
                </Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {campsForKind.slice(0, 20).map((c) => {
                    const on = form.campaign_ids.includes(c.id);
                    return (
                      <button key={c.id} type="button" onClick={() => toggleCampaign(c.id)}
                        className={`text-[10px] px-2 py-1 rounded-full border ${on ? 'border-violet-400/40 bg-violet-500/15 text-white' : 'border-white/10 text-muted-foreground'}`}>
                        {c.name || c.id}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[9px] text-muted-foreground mt-1">{form.campaign_ids.length ? `${form.campaign_ids.length} elegidas` : 'Todas las de este tipo'}</p>
              </div>
            )}

            <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-2">
              <Label className="text-xs inline-flex items-center gap-1.5">
                <UserCheck className="w-3.5 h-3.5" /> ¿Cuándo se lo asignamos a un vendedor?
                <FieldTooltip tip="Siempre: entra al vendedor de inmediato. Al reaccionar: se lo damos cuando conteste. Si no contesta: al final lo asignas o lo mandas a Perdido/Frío para no gastar tiempo." />
              </Label>
              {[
                { v: 'always', t: 'Siempre, desde el primer mensaje', d: 'Como hoy. El vendedor lo ve de inmediato.' },
                { v: 'first_reaction', t: 'Hasta que el lead reaccione', d: 'Formulario: cuando conteste. WhatsApp: cuando vuelva a escribir después de la IA.' },
                { v: 'end_or_lost', t: 'Si no contesta, ciérralo o asígnalo al final', d: 'No quemes al equipo con alguien que jamás respondió.' },
              ].map((opt) => (
                <label key={opt.v} className={`flex gap-2 rounded-md border p-2 cursor-pointer ${form.assignment_policy === opt.v ? 'border-violet-400/40 bg-violet-500/10' : 'border-white/5'}`}>
                  <input type="radio" className="mt-0.5" checked={form.assignment_policy === opt.v} onChange={() => setForm({ ...form, assignment_policy: opt.v })} />
                  <span>
                    <span className="text-[11px] text-white block">{opt.t}</span>
                    <span className="text-[9px] text-muted-foreground">{opt.d}</span>
                  </span>
                </label>
              ))}
              {form.assignment_policy === 'end_or_lost' && (
                <Select value={form.lost_stage_key} onValueChange={(v) => setForm({ ...form, lost_stage_key: v })}>
                  <SelectTrigger className="bg-black/30 border-white/10 text-xs h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="perdido" className="text-xs">Mover a Perdido</SelectItem>
                    <SelectItem value="frio" className="text-xs">Mover a Frío / ilocalizable</SelectItem>
                    <SelectItem value="assign" className="text-xs">Asignarlo igual al vendedor</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="flex items-center gap-2 rounded-md bg-white/[0.02] border border-white/5 p-2">
              <Switch checked={form.cancel_on_reply} onCheckedChange={(v) => setForm({ ...form, cancel_on_reply: v })} data-testid="recipe-cancel-on-reply" />
              <Label className="text-xs">Si el lead responde, se corta el resto de mensajes</Label>
            </div>

            {wabas.length > 1 && form.kind === KIND_FORM && (
              <div>
                <Label className="text-[10px] text-muted-foreground">Número de WhatsApp (para elegir plantillas)</Label>
                <Select value={wabaSel ? `${wabaSel.provider}:${wabaSel.id}` : ''} onValueChange={(v) => { const i = v.indexOf(':'); setWabaSel({ provider: v.slice(0, i), id: v.slice(i + 1) }); }}>
                  <SelectTrigger className="bg-black/20 border-white/10 text-xs h-8 mt-1"><SelectValue placeholder="Selecciona número" /></SelectTrigger>
                  <SelectContent>
                    {wabas.map((w) => (
                      <SelectItem key={`${w.provider}:${w.id}`} value={`${w.provider}:${w.id}`} className="text-xs">{w.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs inline-flex items-center gap-1.5">
                Mensajes (máx. {MAX_STEPS[form.kind]} en 24 h)
                <FieldTooltip tip={form.kind === KIND_FORM
                  ? 'Plantillas aprobadas. Ej: 1) “Hola María, ¿seguimos?” a las 2 h. 2) Recordatorio a las 6 h. 3) Último aviso a las 20 h.'
                  : 'Texto libre porque él empezó. Ej: “¿Sigues por aquí? Te dejo la ficha.” + foto del depto.'} />
              </Label>
              {form.steps.map((s, i) => (
                <div key={i} className="rounded-lg bg-white/[0.02] border border-white/5 p-3 space-y-2" data-testid={`recipe-step-${i}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-1 text-[11px] text-white/80">
                      Esperar
                      <Input type="number" min={0} value={s.delay_minutes} onChange={(e) => updateStep(i, { delay_minutes: parseInt(e.target.value, 10) || 0 })} className="bg-black/20 border-white/10 text-[11px] h-7 w-20 mx-1" data-testid={`recipe-step-${i}-delay`} />
                      minutos
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-[10px]"
                        data-testid={`recipe-step-${i}-email`}
                        onClick={() => {
                          if (!emails.length) {
                            toast.error('Primero guarda un correo en Correos / Mailings. Aquí solo se elige uno ya hecho.');
                            return;
                          }
                          updateStep(i, { channel: s.channel === 'email' ? 'whatsapp' : 'email' });
                        }}
                      >
                        <Mail className="w-3 h-3 mr-1" />{s.channel === 'email' ? 'WhatsApp' : 'Correo'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-7 text-[10px] ${s.channel === 'llamada_ia' ? 'text-emerald-300' : ''}`}
                        data-testid={`recipe-step-${i}-llamada`}
                        onClick={() => updateStep(i, {
                          channel: s.channel === 'llamada_ia' ? 'whatsapp' : 'llamada_ia',
                          template: null,
                        })}
                      >
                        <Phone className="w-3 h-3 mr-1" />{s.channel === 'llamada_ia' ? 'WhatsApp' : 'Llamada IA'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-7 text-[10px] ${s.channel === 'conference' ? 'text-sky-300' : ''}`}
                        data-testid={`recipe-step-${i}-conference`}
                        onClick={() => updateStep(i, {
                          channel: s.channel === 'conference' ? 'whatsapp' : 'conference',
                          template: null,
                        })}
                      >
                        <Phone className="w-3 h-3 mr-1" />{s.channel === 'conference' ? 'WhatsApp' : 'Conferencia'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-7 text-[10px] ${s.channel === 'flow' ? 'text-fuchsia-300' : ''}`}
                        data-testid={`recipe-step-${i}-flow`}
                        onClick={() => updateStep(i, { channel: s.channel === 'flow' ? 'whatsapp' : 'flow', template: null })}
                      >
                        <Workflow className="w-3 h-3 mr-1" />{s.channel === 'flow' ? 'WhatsApp' : 'Flow'}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => removeStep(i)} data-testid={`recipe-step-${i}-remove`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>

                  {(s.channel === 'llamada_ia' || s.channel === 'conference') && (
                    <div className="space-y-1 mb-2">
                      <Label className="text-[10px] text-white/70 inline-flex items-center gap-1">
                        ¿Cuándo aplica este paso?
                        <FieldTooltip tip="Dentro de horario = hay un vendedor de turno ahora mismo. Fuera = no hay nadie de guardia. Si la condición no se cumple, el paso se OMITE y pasa al siguiente — pon un paso de respaldo para el otro caso (ej: Conferencia dentro de horario y Llamada IA fuera). Si no configuras turnos de guardia, siempre cuenta como 'dentro'." />
                      </Label>
                      <Select value={s.when || 'cualquiera'} onValueChange={(v) => updateStep(i, { when: v })}>
                        <SelectTrigger className="bg-black/20 border-white/10 text-xs h-8" data-testid={`recipe-step-${i}-when`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cualquiera" className="text-xs">Siempre (a cualquier hora)</SelectItem>
                          <SelectItem value="dentro_horario" className="text-xs">Solo dentro de horario (hay vendedor de turno)</SelectItem>
                          <SelectItem value="fuera_horario" className="text-xs">Solo fuera de horario (no hay guardia)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {i > 0 && (
                    <div className="space-y-1 mb-2">
                      <Label className="text-[10px] text-white/70 inline-flex items-center gap-1">
                        ¿Cuándo ejecutar este paso?
                        <FieldTooltip tip="'Siempre' lo ejecuta pase lo que pase. 'Solo si el paso anterior no obtuvo respuesta' es el rescate: si el paso anterior fue una llamada y el lead NO contestó, este paso entra; si sí contestó (o ya respondió por otro canal), se omite. Ideal: Conferencia con el vendedor y, si no contesta, que entre la IA de voz." />
                      </Label>
                      <Select value={s.solo_si || 'siempre'} onValueChange={(v) => updateStep(i, { solo_si: v })}>
                        <SelectTrigger className="bg-black/20 border-white/10 text-xs h-8" data-testid={`recipe-step-${i}-solo-si`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="siempre" className="text-xs">Siempre</SelectItem>
                          <SelectItem value="sin_respuesta" className="text-xs">Solo si el paso anterior no obtuvo respuesta</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {s.channel === 'conference' ? (
                    <div className="space-y-1">
                      <Label className="text-[10px] text-white/70 inline-flex items-center gap-1">
                        Enlaza al lead con un vendedor de turno
                        <FieldTooltip tip="Marca al vendedor de guardia y lo conecta con el lead (conferencia). Prefiere al vendedor asignado si está en turno; si no, cualquiera de guardia. Si NO hay nadie de guardia, el paso no se envía — nunca deja al lead en una sala vacía. Requiere plan con voz." />
                      </Label>
                      <p className="text-[10px] text-muted-foreground">
                        Ideal para “dentro de horario”: atiende un humano. Combínalo con un paso de Llamada IA “fuera de horario” como respaldo.
                      </p>
                    </div>
                  ) : s.channel === 'llamada_ia' ? (
                    <div className="space-y-1">
                      <Label className="text-[10px] text-white/70 inline-flex items-center gap-1">
                        Qué debe lograr la IA en la llamada
                        <FieldTooltip tip="La IA usa el prompt de tu Agente IA y las funciones activas. No se pasa al vendedor: si el lead pide un asesor y el equipo ya cerró, anota y se llama al abrir. Ej: “Confirmar si sigue interesado en el depa de Polanco.”" />
                      </Label>
                      <Textarea
                        value={s.text || ''}
                        onChange={(e) => updateStep(i, { text: e.target.value, channel: 'llamada_ia' })}
                        placeholder="Ej: Confirmar interés y agendar visita. Si no contesta, no insistas más de una vez."
                        className="bg-black/20 border-white/10 text-xs min-h-[72px]"
                        data-testid={`recipe-step-${i}-objetivo`}
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Necesitas número de voz en Ajustes. El lead oye a la IA, no un menú de “marca 1”.
                      </p>
                    </div>
                  ) : s.channel === 'email' ? (
                    emails.length ? (
                    <Select value={s.email_template_id || ''} onValueChange={(v) => updateStep(i, { email_template_id: v, channel: 'email' })}>
                      <SelectTrigger className="bg-black/20 border-white/10 text-xs h-8"><SelectValue placeholder="Elige un correo ya guardado" /></SelectTrigger>
                      <SelectContent>
                        {emails.map((p) => (
                          <SelectItem key={p.id} value={p.id} className="text-xs">{p.nombre || p.asunto || p.id}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    ) : (
                      <p className="text-[10px] text-amber-300">No hay plantillas. Ve a <a className="underline" href="/correo">Correos</a>, guarda una y vuelve.</p>
                    )
                  ) : s.channel === 'flow' ? (
                    <div className="space-y-2">
                      {myFlows.length === 0 ? (
                        <p className="text-[10px] text-amber-300">Aún no tienes flows publicados en tu número. Créalos y publícalos en <a className="underline" href="/backoffice/gupshup-plantillas">Plantillas y Flows</a>.</p>
                      ) : (
                        <>
                          <Label className="text-[10px] text-white/70 inline-flex items-center gap-1">Formulario (Flow) a enviar
                            <FieldTooltip tip="Un Flow es un formulario interactivo dentro de WhatsApp. Solo aparecen los tuyos ya PUBLICADOS. El lead debe estar en conversación (ventana de 24h) para recibirlo." />
                          </Label>
                          <Select value={s.flow_id || ''} onValueChange={(v) => { const f = myFlows.find((x) => x.flow_id === v); updateStep(i, { flow_id: v, flow_app_id: f ? f.flow_app_id : '', flow_screen: f ? f.first_screen : '' }); }}>
                            <SelectTrigger className="bg-black/20 border-white/10 text-xs h-8" data-testid={`recipe-step-${i}-flow-select`}><SelectValue placeholder="Elige un flow publicado" /></SelectTrigger>
                            <SelectContent>
                              {myFlows.map((f) => (<SelectItem key={f.flow_id} value={f.flow_id} className="text-xs">{f.name}</SelectItem>))}
                            </SelectContent>
                          </Select>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[10px] text-white/70">¿A quién?</Label>
                              <Select value={s.flow_recipient || 'lead'} onValueChange={(v) => updateStep(i, { flow_recipient: v })}>
                                <SelectTrigger className="bg-black/20 border-white/10 text-xs h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="lead" className="text-xs">Al lead</SelectItem>
                                  <SelectItem value="asignado" className="text-xs">Al vendedor asignado</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-[10px] text-white/70">Texto del botón</Label>
                              <Input value={s.flow_cta || ''} onChange={(e) => updateStep(i, { flow_cta: e.target.value })} placeholder="Abrir" className="bg-black/20 border-white/10 text-xs h-8" />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ) : form.kind === KIND_FORM ? (
                    <div className="space-y-2">
                      <TemplateSelector
                        provider={wabaSel?.provider || 'gupshup'}
                        configId={wabaSel?.provider === 'gupshup' ? wabaSel.id : undefined}
                        wabaId={wabaSel?.provider === 'twilio' ? wabaSel.id : undefined}
                        moduleType="logicas"
                        value={s.template?.stable_ref || ''}
                        onSelect={(p) => {
                          const indexes = (p.variables?.length)
                            ? [...new Set(p.variables.map(Number).filter(Number.isFinite))].sort((a, b) => a - b)
                            : extractTemplateIndexes(p.body || '');
                          updateStep(i, {
                            template: { provider: p.provider, stable_ref: p.stable_ref, language: p.language, variable_mapping: normalizeVariableMapping(indexes, s.template?.variable_mapping || []) },
                            _tplBody: p.body || '',
                          });
                        }}
                      />
                      {s.template?.stable_ref && (s.template?.variable_mapping?.length > 0) && (
                        <div className="space-y-1.5 rounded-md bg-black/20 border border-white/5 p-2">
                          {s.template.variable_mapping.map((row) => {
                            const idx = Number(row.index);
                            return (
                              <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                                <div className="col-span-2 text-[10px]">{`{{${idx}}}`}</div>
                                <div className="col-span-5">
                                  <Select value={row.source} onValueChange={(value) => setMappingRow(i, idx, { source: value, custom_value: value === 'custom' ? (row.custom_value || '') : '' })}>
                                    <SelectTrigger className="bg-black/20 border-white/10 text-[10px] h-7"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      {WA_VARIABLE_SOURCES.map((opt) => (<SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="col-span-5">
                                  {row.source === 'custom'
                                    ? <Input value={row.custom_value || ''} onChange={(e) => setMappingRow(i, idx, { custom_value: e.target.value })} className="bg-black/20 border-white/10 text-[10px] h-7" />
                                    : <p className="text-[9px] text-muted-foreground">Ej: {sourceExample(row.source, row.custom_value)}</p>}
                                </div>
                              </div>
                            );
                          })}
                          <p className="text-[10px] text-white/80 whitespace-pre-wrap">{recipePreview(s._tplBody, s.template.variable_mapping)}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Textarea value={s.text} onChange={(e) => updateStep(i, { text: e.target.value })} rows={3} className="bg-black/20 border-white/10 text-xs" placeholder="Ej: Hola María, ¿sigues interesada? Te dejo la ficha del depto." data-testid={`recipe-step-${i}-text`} />
                      <div className="flex items-center gap-2">
                        <label className="text-[10px] text-muted-foreground inline-flex items-center gap-1 cursor-pointer">
                          <ImageIcon className="w-3 h-3" />
                          {uploading === i ? 'Subiendo…' : (s.media_url ? 'Cambiar foto/video' : 'Adjuntar foto o video')}
                          <input type="file" accept="image/*,video/*" className="hidden" onChange={(e) => uploadMedia(i, e.target.files?.[0])} />
                        </label>
                        {s.media_url && <span className="text-[9px] text-emerald-400 truncate max-w-[160px]">Adjunto listo</span>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" className="text-[10px] border-white/10" onClick={addStep} data-testid="recipe-add-step"><Plus className="w-3 h-3 mr-1" />Agregar mensaje</Button>
            </div>

            <Button size="sm" className="w-full text-xs" onClick={save} data-testid="recipe-save">
              <MessageSquare className="w-3.5 h-3.5 mr-1" />{editing ? 'Guardar cambios' : 'Crear receta'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
